import {
  CreateEmailAddressForm,
  createEmailAddressSchema,
  EmailAddressDto,
  UpdateEmailAddressForm,
  updateEmailAddressSchema,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { userRequests } from 'redux/requests/users.request';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from './components/SharedDialogHookComponents';
import { getDeleteConfirmationDialogConfig } from './helpers/ConfirmationalertdialogConfig';
import useConfirmationAlertDialog from './hooks/useConfirmationAlertDialog.hook';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { Button } from '@act/storybook';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { SelectOption } from 'stories/forms/Select/ControlledSelect';
import { useToast } from '../app/ReusableToast';
import { useAppDispatch } from 'redux/storeHooks';
import {
  createEmailAddressSuccess,
  deletedEmailAddressSuccess,
  updatedEmailAddressSuccess,
} from 'redux/slices/memberSlice.slice';
import { zodResolver } from '@hookform/resolvers/zod';
import { TrackingProvider } from 'react-components/tracking';

export interface EmailAddressUniqueProps {
  mode: 'Add' | 'Delete' | 'Edit' | 'Set primary';
  memberId: string;
  emailAddresses: EmailAddressDto[];
}

export type EmailAddressDialogProps = GenericDialogProps<boolean> &
  EmailAddressUniqueProps;

export type EmailAddressDialogCallbackInput = {};

export interface EmailAddressForm {
  emailAddressId: number;
  emailAddress: string;
  description: string;
  primary: boolean;
}

export const EmailAddressDialog = ({
  mode,
  memberId,
  emailAddresses,
  open,
  setOpen,
  callback,
}: EmailAddressDialogProps) => {
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [createEmailAddressResult, isCreating, createEmailAddress] = useApiCall(
    userRequests.createEmailAddress
  );
  const [updateEmailAddressResult, isUpdating, updateEmailAddress] = useApiCall(
    userRequests.updateEmailAddress
  );
  const [deleteEmailAddressResult, isDeleting, deleteEmailAddress] = useApiCall(
    userRequests.deleteEmailAddress
  );

  const isLoading = isCreating || isUpdating || isDeleting;

  const emailAddressOptions: SelectOption<number>[] = emailAddresses?.map(
    ea => {
      const { email: emailAddress, description: label, id: value } = ea;
      return {
        key: label.concat('-', value.toString()),
        label: `${label} <${emailAddress}>`,
        value: value,
      };
    }
  );

  const { control, handleSubmit, setValue, watch, formState } = useForm<
    CreateEmailAddressForm | UpdateEmailAddressForm
  >({
    resolver: zodResolver(
      mode === 'Add' ? createEmailAddressSchema : updateEmailAddressSchema
    ),
    defaultValues: {
      emailAddress: '',
      description: '',
      primary: false,
      emailAddressId: null,
    },
  });

  const emailAddressId: number = watch('emailAddressId');
  const saveIsDisabled = mode !== 'Add' && !emailAddressId;
  const { isDirty } = formState;

  useEffect(() => {
    if (emailAddressId) {
      const selectedEmailAddress = emailAddresses.find(
        ea => ea.id === emailAddressId
      );
      if (selectedEmailAddress) {
        setValue('description', selectedEmailAddress.description);
        setValue('emailAddress', selectedEmailAddress.email);
      }
    }
  }, [emailAddressId]);

  useEffect(() => {
    if (
      createEmailAddressResult ||
      updateEmailAddressResult ||
      deleteEmailAddressResult
    ) {
      if (createEmailAddressResult) {
        dispatch(
          createEmailAddressSuccess({
            memberId,
            emailAddress: createEmailAddressResult,
          })
        );
        toast('Email address has been added successfully');
      }
      if (updateEmailAddressResult) {
        dispatch(
          updatedEmailAddressSuccess({
            memberId,
            emailAddressId: updateEmailAddressResult.id,
            emailAddress: updateEmailAddressResult,
          })
        );
        toast('Email address has been updated successfully');
      }
      if (deleteEmailAddressResult) {
        dispatch(
          deletedEmailAddressSuccess({
            memberId,
            emailAddressId: deleteEmailAddressResult.id,
          })
        );
        toast('Email address has been deleted successfully');
      }
      setOpen(false);
      callback(true);
    }
  }, [
    createEmailAddressResult,
    updateEmailAddressResult,
    deleteEmailAddressResult,
  ]);

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback(true);
      });
    } else {
      setOpen(false);
      callback(true);
    }
  };

  const formConfig: FormConfig<
    CreateEmailAddressForm | UpdateEmailAddressForm
  > = [];
  if (mode !== 'Add') {
    formConfig.push({
      type: 'input',
      name: 'emailAddressId',
      label: 'Select an email address',
      input: 'select',
      props: {
        options: emailAddressOptions,
        fullWidth: true,
        trackingName: 'email-address-select',
      },
    });
  }

  if ((mode === 'Edit' && emailAddressId) || mode === 'Add') {
    formConfig.push({
      type: 'input',
      name: 'description',
      label: 'Description',
      input: 'text',
      props: {
        trackingName: 'email-description-input',
      },
    });
    formConfig.push({
      type: 'input',
      name: 'emailAddress',
      label: 'Email address',
      input: 'text',
      props: {
        trackingName: 'email-address-input',
      },
    });
  }

  async function onSubmit(
    data: CreateEmailAddressForm | UpdateEmailAddressForm
  ) {
    if (mode === 'Add') {
      createEmailAddress({
        params: { memberId },
        body: {
          email: data.emailAddress,
          description: data.description,
        },
      });
    }

    if (mode === 'Delete') {
      const confirmation = await openConfirmationAlertDialog(
        getDeleteConfirmationDialogConfig('email address')
      );
      if (!confirmation) return;

      deleteEmailAddress({
        params: { id: emailAddressId, memberId },
      });
    }

    if (mode === 'Set primary') {
      updateEmailAddress({
        params: { id: emailAddressId, memberId },
        body: {
          primary: true,
          description: data.description,
          email: data.emailAddress,
        },
      });
    }

    if (mode === 'Edit') {
      updateEmailAddress({
        params: { id: emailAddressId, memberId },
        body: {
          email: data.emailAddress,
          description: data.description,
        },
      });
    }
  }

  return (
    <TrackingProvider contextName="email-address-dialog">
      <Box sx={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title={`${mode} email address`}
            handleClose={handleClose}
          />
          <DialogContent>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button
              onClick={handleClose}
              disabled={isLoading}
              trackingName="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color={mode === 'Delete' ? 'error' : 'primary'}
              onClick={handleSubmit(onSubmit)}
              disabled={saveIsDisabled || isLoading}
              debounced
              sx={{ padding: '10px 24px' }}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              trackingName={
                mode === 'Delete'
                  ? 'delete-email-button'
                  : 'save-email-button'
              }
            >
              {isLoading
                ? mode === 'Delete'
                  ? 'Deleting...'
                  : 'Saving...'
                : mode === 'Delete'
                ? 'Delete'
                : 'Save'}
            </Button>
          </DialogActions>
        </DialogContentContainer>
      </Dialog>
    </Box>
    </TrackingProvider>
  );
};
